import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ISubscriptionPlanWithTaxAndDiscountInfo,
  OrderStatus,
  IOrderInfo,
  PaymentStatus,
  ISubscriptionInfo,
  PlanButtonLabel,
} from '../../../models/Subscription';
import {
  BehaviorSubject,
  interval,
  Observable,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectOrdersByPageAndFilters,
  selectOrdersPagination,
  selectOrdersState,
  selectSubscriptionPlans,
} from '../../../store/user/user.reducer';
import {
  BillingHistoryUpdatePaginationAction,
  FetchOrdersAction,
  FetchSubscriptionPlansAction,
  SendInvoiceAction,
} from '../../../store/user/user.actions';
import { AsyncState } from '../../../store';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTabGroup } from '@angular/material/tabs';
import {
  formatAmount,
  getCurrencySymbol,
  getOrderStatusLabel,
  getPaymentStatusLabel,
} from '../../../_helpers/common.functions';
import { AuthService } from '../../../services/auth.service';
import * as userActions from '../../../store/user/user.actions';
import { Actions, ofType } from '@ngrx/effects';
import { SnackBarService } from '../../../services/snackbar.service';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { getCardInfo } from '../subscription-plan-card/subscription-plan-card.component';
import { MatDialog } from '@angular/material/dialog';
import { UnsavedChangesDialogComponent } from '../unsaved-changes-dialog/unsaved-changes-dialog.component';
import { IUserInfo } from '../../../models/user';
import { TranslateService } from '@ngx-translate/core';
import { SalesService } from '../../../services/sales.services';
import { IMyOrganizationSubscription } from '../../../models/organization';
import {
  isNeverDate,
  toServerDate,
} from '../../shared/pipes/server-date.pipes';

interface TableColumn {
  header: string;
  bindingValue: string;
}

export enum SubscriptionTabs {
  PLANS = 'plans',
  BILLING_HISTORY = 'billingHistory',
}

// To Display in the Billing Orders Table
interface IOrderInfoWithMetaData extends IOrderInfo {
  PlanName: string;
  Action: string;
  FormattedTotalAmount: string;
  OrderStatusFormattedValue: string;
  PaymentStatusFormattedValue: string;
  StartDate: string;
  EndDate: string;
}

@Component({
  selector: 'app-subscription',
  standalone: false,
  templateUrl: './subscription.component.html',
  styleUrl: './subscription.component.scss',
})
export class SubscriptionComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  activeTab: SubscriptionTabs = SubscriptionTabs.PLANS;
  private destroy$ = new Subject<void>();
  private _snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private salesService = inject(SalesService);
  showSubscriptionAsDialog = false;
  getCardInfo = getCardInfo;

  // Set when an organization provides the user's plan - they cannot buy or change it here
  organizationSubscription: IMyOrganizationSubscription | null = null;

  subscriptions: {
    state: AsyncState;
    plans: ISubscriptionPlanWithTaxAndDiscountInfo[];
  } = {
    state: { status: 'NotFetched' },
    plans: [],
  };
  // Orders State
  ordersState: AsyncState = { status: 'NotFetched' };
  private _componentSubscriptions: Subscription[] = [];

  hasPendingOrder = false;
  isCancellingAutoRenewal = false;
  private pendingOrderCreatedAt: number | null = null;
  private pollingSubscription: Subscription | null = null;
  private readonly TRIAL_MONTHS = 1;
  private readonly POLLING_INTERVAL_MS = 30_000;
  private readonly PENDING_ORDER_TIMEOUT_MS = 15 * 60 * 1000;

  // Table Columns Configruation For Order History Table
  billingHistoryTableColumns: TableColumn[] = [
    { header: 'Order #', bindingValue: 'Name' },
    { header: 'Created Date', bindingValue: 'CreatedAt' },
    { header: 'Plan', bindingValue: 'PlanName' },
    { header: 'Start Date', bindingValue: 'StartDate' },
    { header: 'End Date', bindingValue: 'EndDate' },
    { header: 'Total Amount', bindingValue: 'FormattedTotalAmount' },
    { header: 'Order Status', bindingValue: 'OrderStatusFormattedValue' },
    { header: 'Payment Status', bindingValue: 'PaymentStatusFormattedValue' },
    { header: 'Actions', bindingValue: 'action' }, // Do Not Change this column
  ];
  displayedColumns: string[] = this.billingHistoryTableColumns.map(
    (column) => column.bindingValue,
  );
  dataSource = new MatTableDataSource<IOrderInfoWithMetaData>([]);

  // Billing History Paginator Related Information
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  disabled = false;
  totalItemsLength = 0;
  pageSize = 10;
  pageIndex = 0;
  readonly MIN_ITEMS_PER_PAGE = 10;
  readonly MAX_ITEMS_PER_PAGE = 50;
  pageSizeOptions: number[] = [10];
  hidePageSize = false;
  pageEvent!: PageEvent;

  // Pagination subject to trigger orders$ updates
  private paginationSubject$ = new BehaviorSubject<{
    pageNumber: number;
    pageSize: number;
  }>({
    pageNumber: this.pageIndex + 1,
    pageSize: this.pageSize,
  });

  // Observables
  subscriptions$: Observable<{
    state: AsyncState;
    plans: ISubscriptionPlanWithTaxAndDiscountInfo[];
  }>;
  orders$: Observable<IOrderInfo[]>;
  pagination$: Observable<{
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  } | null>;
  ordersState$: Observable<AsyncState>;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private actions$: Actions,
    private authService: AuthService,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
  ) {
    this.subscriptions$ = this.store.select(selectSubscriptionPlans);
    this.pagination$ = this.store.select(selectOrdersPagination);
    this.ordersState$ = this.store.select(selectOrdersState);
    this.orders$ = this.paginationSubject$.pipe(
      switchMap(({ pageNumber, pageSize }) =>
        this.store.select(
          selectOrdersByPageAndFilters(pageNumber, pageSize, 'all', 'all'),
        ),
      ),
    );

    const nav = this.router.getCurrentNavigation();
    this.showSubscriptionAsDialog =
      nav?.extras?.state?.['showSubscriptionAsDialog'] ?? false;
  }

  onSkipSubscriptionBtnClick() {
    this.router.navigate(['/books']);
  }

  onRefreshSubscriptionBtnClick() {
    this.store.dispatch(
      userActions.RefreshTokenAction({
        request: {
          Token: this.authService.getTokenInformation()?.RefreshToken!,
          RefreshParentalControl: true,
        },
      }),
    );
  }

  get isAdmin() {
    const tokenInfo = this.authService.getDecodedToken();
    if (tokenInfo) {
      return (
        (tokenInfo?.['UserInfo'] as IUserInfo)?.Roles.findIndex(
          (r) => r.RoleName == 'Admin',
        ) > -1
      );
    }
    return false;
  }

  // Check If the User Plan Has Expired
  isUserPlanExpired(): boolean {
    const tokenInfo = this.authService.getDecodedToken();
    const primaryId = tokenInfo?.['UserInfo']?.PrimarySubscription;
    const subscriptions: ISubscriptionInfo[] | undefined =
      tokenInfo?.['UserInfo']?.Subscriptions;

    return (
      !!primaryId &&
      !!subscriptions?.find(
        (sub) => sub.Id === primaryId && sub.Status === 'Expired',
      )
    );
  }

  // The token's Status is worked out when the token is issued, so a one-time (Bundle)
  // plan that ends while the token is still valid would keep showing as active.
  // Its end date decides it as well.
  private isSubscriptionExpired(sub: ISubscriptionInfo): boolean {
    if (sub.Status === 'Expired') return true;
    if (sub.BillingCycle?.toLowerCase() !== 'one-time' || !sub.EndDate) {
      return false;
    }
    // The API sends UTC dates without a zone
    const endDate = new Date(
      sub.EndDate.endsWith('Z') ? sub.EndDate : sub.EndDate + 'Z',
    );
    return !isNaN(endDate.getTime()) && endDate.getTime() <= Date.now();
  }

  private loadOrganizationSubscription(): void {
    if (this.isAdmin) return;
    this.salesService
      .getMyOrganizationSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) =>
          (this.organizationSubscription =
            response?.Value?.Subscription ?? null),
        error: () => (this.organizationSubscription = null),
      });
  }

  /**
   * The organization managing the user's plan, for "· managed by <b>name</b>"
   * after the plan's message - null for a user who manages their own.
   */
  get managingOrganizationName(): string | null {
    return this.organizationSubscription?.OrganizationName || null;
  }

  /** Whole days from today until a server (UTC) date, never below 0. */
  private daysUntil(value: string): number {
    const date = toServerDate(value);
    if (!date) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.max(
      0,
      Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)),
    );
  }

  /**
   * The same messages a user's own plan gets, from the organization's dates:
   * "Bundle Plan starts in N days", then "Next renewal in N days". A plan
   * that never ends (a bundle) has no countdown - the template's
   * "Managed by <name>" says it all.
   */
  private getOrganizationPlanInfoText(
    org: IMyOrganizationSubscription,
  ): string {
    if (!org.IsActive) {
      return 'This plan has ended. Contact your organization to renew it.';
    }

    const startsIn = this.daysUntil(org.StartDate);
    if (startsIn) {
      return this.translate.instant(
        startsIn == 1
          ? 'Subscriptions.Labels.PlanStartMsg'
          : 'Subscriptions.Labels.PlanStartMsgPlural',
        { planType: org.PlanName || 'Organization', days: startsIn },
      );
    }

    if (isNeverDate(org.EndDate)) return '';

    const endsIn = this.daysUntil(org.EndDate);
    return this.translate.instant(
      endsIn == 0
        ? 'Subscriptions.Labels.RemainingDayZeroMsg'
        : endsIn == 1
        ? 'Subscriptions.Labels.RemainingDayMsg'
        : 'Subscriptions.Labels.RemainingDaysMsg',
      { days: endsIn },
    );
  }

  // Days left in the free trial (one month from the Free subscription's start), or null when the
  // user is not in one. A trial is the Free subscription given at sign-up that has not ended, for a user who never held a paid plan:
  // anyone with a paid subscription in their history (even an expired one) is not in a trial.
  getTrialDaysRemaining(): number | null {
    const subscriptions: ISubscriptionInfo[] | undefined =
      this.authService.getDecodedToken()?.['UserInfo']?.Subscriptions;
    if (!subscriptions?.length) return null;

    const isFree = (sub: ISubscriptionInfo) =>
      (sub.SubscriptionType ?? '').toLowerCase() === 'free';
    if (subscriptions.some((sub) => !isFree(sub))) return null;

    // The trial runs one calendar month from the Free subscription's start date
    const DAY_MS = 1000 * 60 * 60 * 24;
    const trialEndTimes = subscriptions
      .filter((sub) => !this.isSubscriptionExpired(sub) && !!sub.StartDate)
      .map((sub) => {
        const trialEnd = new Date(
          sub.StartDate.endsWith('Z') ? sub.StartDate : sub.StartDate + 'Z',
        );
        trialEnd.setMonth(trialEnd.getMonth() + this.TRIAL_MONTHS);
        return trialEnd.getTime();
      })
      .filter((time) => !isNaN(time) && time > Date.now());
    if (!trialEndTimes.length) return null;

    return Math.ceil((Math.max(...trialEndTimes) - Date.now()) / DAY_MS);
  }

  // Get the Current Plan From the User Primary Subscription
  getSelectedPlanChipLabel(): string | null {
    const tokenInfo = this.authService.getDecodedToken();
    const primaryId = tokenInfo?.['UserInfo']?.PrimarySubscription;
    const subscriptions: ISubscriptionInfo[] | undefined =
      tokenInfo?.['UserInfo']?.Subscriptions;

    // An organization's plan, like anyone's: Free until it starts (a month
    // after the organization pays) or once it has ended, then shown by its
    // name ("Monthly Plan", "Bundle Plan")
    const org = this.organizationSubscription;
    if (org) {
      if (!org.IsActive || this.daysUntil(org.StartDate) > 0) {
        return 'Free Plan';
      }
      return `${org.PlanName || 'Organization'} Plan`;
    }

    if (!primaryId || !subscriptions) return null;

    // return Free plan if user plan is expired
    if (this.isUserPlanExpired()) {
      return 'Free Plan';
    }

    // return Free plan if user plan is not yet started
    const activeSubscriptionPlan = subscriptions.find(
      (sub) => sub.Id === primaryId,
    );

    // return Free plan if user plan is expired
    if (this.isUserPlanExpired()) {
      return 'Free Plan';
    }

    // Handle the Bundle Plan
    if (activeSubscriptionPlan?.BillingCycle?.toLowerCase() === 'one-time') {
      return this.getPlanLabel(activeSubscriptionPlan);
    }

    // return Free plan if user plan is not yet started

    if (activeSubscriptionPlan) {
      let today = new Date();
      let startDate = new Date(activeSubscriptionPlan.StartDate);

      // Remove time portion to calculate full days accurately
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);

      // check if plan has started yet or not
      let diffTime = startDate.getTime() - today.getTime();
      let diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

      if (diffDays) {
        return `Free Plan`;
      }
    }

    const billingCycle = activeSubscriptionPlan?.BillingCycle ?? 'Free';
    return `${billingCycle} Plan`;
  }

  // "Bundle Plan" for the one-time bundle, otherwise the billing cycle: "Monthly Plan"
  private getPlanLabel(subscription: ISubscriptionInfo): string {
    if (subscription.BillingCycle?.toLowerCase() === 'one-time') {
      const bundlePlan = this.subscriptions.plans.find(
        (p) =>
          p.Id == subscription.SubscriptionPlanId &&
          p.Name.toLowerCase() === 'bundle',
      );
      return `${bundlePlan?.Name ?? 'Bundle'} Plan`;
    }
    return `${subscription.BillingCycle ?? 'Free'} Plan`;
  }

  getSelectedPlanInfoText(): string | null {
    const tokenInfo = this.authService.getDecodedToken();
    const primaryId = tokenInfo?.['UserInfo']?.PrimarySubscription;
    const subscriptions: ISubscriptionInfo[] | undefined =
      tokenInfo?.['UserInfo']?.Subscriptions;
    const activeSubscriptionPlan = subscriptions?.find(
      (p) => p.Id == primaryId,
    );

    // An organization user sees the same messages as anyone else (the template
    // adds "managed by" the organization), except when the plan has ended:
    // upgrading is for the organization to do, not them.
    const org = this.organizationSubscription;
    if (org) {
      return this.getOrganizationPlanInfoText(org);
    }

    const upgradeMsg = this.translate.instant(
      'Subscriptions.Labels.UpgradeMsg',
    );

    if (
      !primaryId ||
      !subscriptions ||
      (activeSubscriptionPlan &&
        this.isSubscriptionExpired(activeSubscriptionPlan)) ||
      !activeSubscriptionPlan?.BillingCycle
    )
      return upgradeMsg;

    // Handle One-Time Bundle Plan
    if (activeSubscriptionPlan.BillingCycle.toLowerCase() === 'one-time') {
      const expiryDate = new Date(activeSubscriptionPlan.EndDate);

      if (isNaN(expiryDate.getTime())) {
        return upgradeMsg;
      }

      return `Valid till ${expiryDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`;
    }

    if (activeSubscriptionPlan) {
      let today = new Date();
      let startDate = new Date(activeSubscriptionPlan.StartDate);
      let expiryDate = new Date(activeSubscriptionPlan.EndDate);

      // Remove time portion to calculate full days accurately
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);

      // check if plan has started yet or not
      let diffTime = startDate.getTime() - today.getTime();
      let diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

      if (diffDays) {
        const billingCycle =
          subscriptions.find((sub) => sub.Id === primaryId)?.BillingCycle ??
          'Free';

        let trString =
          diffDays == 0
            ? 'Subscriptions.Labels.PlanStartTodayMsg'
            : diffDays == 1
            ? 'Subscriptions.Labels.PlanStartMsg'
            : 'Subscriptions.Labels.PlanStartMsgPlural';

        return this.translate.instant(trString, {
          planType: billingCycle,
          days: diffDays,
        });
      }

      // else, calculate the next renewal days
      diffTime = expiryDate.getTime() - today.getTime();
      diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

      let trString =
        diffDays == 0
          ? 'Subscriptions.Labels.RemainingDayZeroMsg'
          : diffDays == 1
          ? 'Subscriptions.Labels.RemainingDayMsg'
          : 'Subscriptions.Labels.RemainingDaysMsg';

      return this.translate.instant(trString, {
        days: diffDays,
      });
    }

    return null;
  }

  generatePageSizeOptions(
    minItemsPerPage: number,
    maxItemsPerPage: number,
    totalItemsLength: number,
  ): number[] {
    if (totalItemsLength === 0) {
      return [minItemsPerPage];
    }

    let effectiveMax = maxItemsPerPage;
    if (totalItemsLength < maxItemsPerPage) {
      effectiveMax = Math.ceil(totalItemsLength / 10) * 10;
    }

    const options: number[] = [];
    for (let size = minItemsPerPage; size <= effectiveMax; size += 10) {
      options.push(size);
    }
    return options;
  }

  fetchOrdersIfNeeded() {
    const currentPage = this.pageIndex + 1;
    const currentSize = this.pageSize;
    const orderStatus = 'all';
    const paymentStatus = 'all';

    // Check store for existing orders
    this.store
      .select(
        selectOrdersByPageAndFilters(
          currentPage,
          currentSize,
          orderStatus,
          paymentStatus,
        ),
      )
      .pipe(take(1))
      .subscribe((orders) => {
        if (orders && orders.length > 0) {
          if (this.paginator) {
            this.paginator.pageIndex = this.pageIndex;
            this.paginator.pageSize = this.pageSize;
            this.paginator.length = this.totalItemsLength;
          }
          return;
        }

        this.store.dispatch(
          FetchOrdersAction({
            request: {
              PageNumber: currentPage,
              PageSize: currentSize,
            },
          }),
        );
      });
  }

  formatDateForBillingTable(dateString: string, showTime = false): string {
    if (!dateString) return '';
    const date = new Date(dateString + 'Z'); // UTC interpretation
    if (isNaN(date.getTime())) return '-';

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');

    if (!showTime) return `${yyyy}-${mm}-${dd}`;

    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  }

  getRowActionLabel(order: IOrderInfo): string {
    switch (order.Status) {
      case OrderStatus.Draft:
        return 'Proceed';
      case OrderStatus.InProgress:
        return 'Track';
      case OrderStatus.OnHold:
        return 'Resume';
      case OrderStatus.Completed:
        return 'Send Invoice';
      case OrderStatus.Cancelled:
        return 'Review';
      case OrderStatus.Returned:
        return 'Details';
      default:
        return 'Action';
    }
  }

  onSubscriptionPlanClick(
    plan: ISubscriptionPlanWithTaxAndDiscountInfo,
    action: string,
  ) {
    if (this.hasPendingOrder || this.organizationSubscription) return;
    // Cancelling auto-renewal needs no billing details, so it skips the email check below.
    if (action === PlanButtonLabel.Cancel) {
      this.confirmCancelAutoRenewal(plan);
      return;
    }
    // Auto-renewal cancelled and its paid period still running: nothing can be bought
    // until it ends (the backend refuses it as well).
    const cancelledPlan = this.getCancelledPlanStillRunning();
    if (cancelledPlan && action !== PlanButtonLabel.Active) {
      this.snackBarService.showSnackBar(
        'warning',
        'Auto-renewal for your current plan was cancelled. You can purchase a plan once its access ends.',
      );
      return;
    }
    const tokenInfo = this.authService.getDecodedToken();
    if (tokenInfo) {
      const email = tokenInfo['UserInfo']['Email'];
      if (!email || email === '') {
        this._snackBar.openFromComponent(UpdateEmailSnackbarComponent, {
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['email-snackbar'],
        });
        return;
      }
    }
    switch (action) {
      case PlanButtonLabel.Subscribe:
      case PlanButtonLabel.Buy:
      case PlanButtonLabel.Change:
      case PlanButtonLabel.Complete: // show billing and shipping address page, to allow user to update address, if needed
        this.router.navigate(['subscriptions', 'create'], {
          queryParams: {
            subscriptionId: plan.Id,
          },
          state: {
            showSubscriptionAsDialog: this.showSubscriptionAsDialog,
          },
        });
        break;
      case PlanButtonLabel.Renew:
        this.router.navigate(['subscriptions', 'create'], {
          queryParams: {
            subscriptionId: plan.Id,
          },
          state: {
            oldSubscriptionId: plan.SubscriptionId,
            showSubscriptionAsDialog: this.showSubscriptionAsDialog,
          },
        });
        break;
      case PlanButtonLabel.Active:
        return;
      default:
        this.router.navigate(['subscriptions', 'create'], {
          queryParams: {
            subscriptionId: plan.Id,
          },
          state: {
            showSubscriptionAsDialog: this.showSubscriptionAsDialog,
          },
        });
        break;
    }
  }

  // The plan whose auto-renewal was cancelled while access remains: the backend sends it as
  // status "Cancelled" with the "Active" action (once ended it comes back as "Expired")
  private getCancelledPlanStillRunning():
    | ISubscriptionPlanWithTaxAndDiscountInfo
    | undefined {
    return this.subscriptions.plans.find(
      (p) =>
        p.AutoRenewalStatus === 'Cancelled' &&
        p.AvailableActions?.[0] === 'Active',
    );
  }

  // Ask before cancelling auto-renewal: billing stops, access runs to the end date
  private confirmCancelAutoRenewal(
    plan: ISubscriptionPlanWithTaxAndDiscountInfo,
  ): void {
    if (this.isCancellingAutoRenewal) return;
    if (!plan.OrderId) {
      this.snackBarService.showSnackBar(
        'error',
        'Could not find the order for this plan. Please contact support.',
      );
      return;
    }

    const parsedEndDate = plan.EndDate
      ? new Date(plan.EndDate.endsWith('Z') ? plan.EndDate : plan.EndDate + 'Z')
      : null;
    const endDate =
      parsedEndDate && !isNaN(parsedEndDate.getTime())
        ? parsedEndDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        : '';
    const accessMessage =
      endDate && endDate !== '-'
        ? `You will keep access until ${endDate}.`
        : 'You will keep access until the end of the current billing period.';

    this.dialog
      .open(UnsavedChangesDialogComponent, {
        width: '440px',
        panelClass: 'cancel-auto-renew-dialog',
        data: {
          title: 'Cancel auto-renewal?',
          message: `${plan.Name} Plan will not renew and you will not be charged again. ${accessMessage}`,
          confirmButtonLabel: 'Cancel Auto-Renewal',
          cancelButtonLabel: 'Keep Auto-Renewal',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.isCancellingAutoRenewal = true;
        this.store.dispatch(
          userActions.CancelAutoRenewingSubscriptionAction({
            request: { orderId: plan.OrderId!, cancelAtCycleEnd: true },
          }),
        );
      });
  }

  //  Handle the Page Change For  Billing History Paginator
  onPageChange(event: PageEvent): void {
    this.pageEvent = event;
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    if (this.totalItemsLength === 0) {
      this.pageIndex = 0;
      this.pageSize = this.MIN_ITEMS_PER_PAGE;
      this.disabled = true;
      this.dataSource.data = [];
      if (this.paginator) {
        this.paginator.pageIndex = this.pageIndex;
        this.paginator.pageSize = this.pageSize;
        this.paginator.length = this.totalItemsLength;
      }
      return;
    }

    // Update pagination subject
    this.paginationSubject$.next({
      pageNumber: this.pageIndex + 1,
      pageSize: this.pageSize,
    });

    // Save Pagination State to Store
    this.store.dispatch(
      BillingHistoryUpdatePaginationAction({
        pageNumber: this.pageIndex + 1,
        pageSize: this.pageSize,
      }),
    );

    // Check store and fetch if needed
    this.fetchOrdersIfNeeded();
  }

  onActionClick(order: IOrderInfoWithMetaData) {
    if (!order) {
      return;
    }
    if (order.Status === OrderStatus.Completed) {
      this.store.dispatch(
        SendInvoiceAction({
          request: {
            OrderId: order.OrderId,
          },
        }),
      );
      return;
    }
    this.router.navigate(['transactions'], {
      queryParams: {
        transactionId: order.OrderId,
      },
    });
  }

  ngAfterViewInit(): void {
    // Update displayedColumns if billingHistoryTableColumns changes dynamically
    this.displayedColumns = this.billingHistoryTableColumns.map(
      (column) => column.bindingValue,
    );
    // Sync paginator with restored state
    if (this.paginator) {
      this.paginator.pageIndex = this.pageIndex;
      this.paginator.pageSize = this.pageSize;
      this.paginator.length = this.totalItemsLength;
    }
  }

  // When User Changes the Tab
  onTabChange(index: number): void {
    this.activeTab =
      index === 0 ? SubscriptionTabs.PLANS : SubscriptionTabs.BILLING_HISTORY;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.activeTab },
      queryParamsHandling: 'merge',
    });
    // Always dispatch FetchSubscriptionPlansAction
    this.store.dispatch(FetchSubscriptionPlansAction());
    // For billingHistory, fetch orders if subscriptions are loaded and orders not fetched
    if (
      this.activeTab === SubscriptionTabs.BILLING_HISTORY &&
      this.subscriptions.state.status === 'Success' &&
      this.ordersState.status === 'NotFetched'
    ) {
      this.fetchOrdersIfNeeded();
    }
  }

  private checkAndHandlePendingOrder(orders: IOrderInfo[]): void {
    if (!orders?.length) {
      if (this.ordersState.status === 'Success') {
        this.hasPendingOrder = false;
        this.pendingOrderCreatedAt = null;
        this.stopPolling();
      }
      return;
    }

    const lastOrder = [...orders].sort(
      (a, b) =>
        new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime(),
    )[0];

    const isPending =
      lastOrder.PaymentStatus === PaymentStatus.PaymentPending ||
      lastOrder.PaymentStatus == null;

    if (isPending) {
      const orderTime = new Date(lastOrder.CreatedAt + 'Z').getTime();
      const elapsed = Date.now() - orderTime;

      if (elapsed < this.PENDING_ORDER_TIMEOUT_MS) {
        this.hasPendingOrder = true;
        this.pendingOrderCreatedAt = orderTime;
        this.startPolling();
      } else {
        this.hasPendingOrder = false;
        this.pendingOrderCreatedAt = null;
        this.stopPolling();
      }
    } else {
      this.hasPendingOrder = false;
      this.pendingOrderCreatedAt = null;
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollingSubscription) return;
    this.pollingSubscription = interval(this.POLLING_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          this.pendingOrderCreatedAt !== null &&
          Date.now() - this.pendingOrderCreatedAt >=
            this.PENDING_ORDER_TIMEOUT_MS
        ) {
          this.hasPendingOrder = false;
          this.pendingOrderCreatedAt = null;
          this.stopPolling();
          return;
        }
        this.store.dispatch(FetchSubscriptionPlansAction());
        this.store.dispatch(
          FetchOrdersAction({
            request: { PageNumber: 1, PageSize: this.MIN_ITEMS_PER_PAGE },
          }),
        );
      });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  ngOnInit(): void {
    this.loadOrganizationSubscription();

    this._componentSubscriptions.push(
      this.pagination$.subscribe((pagination) => {
        if (pagination) {
          this.pageIndex = pagination.pageNumber - 1;
          this.pageSize = pagination.pageSize;
          this.totalItemsLength = pagination.totalCount;
          this.pageSizeOptions = this.generatePageSizeOptions(
            this.MIN_ITEMS_PER_PAGE,
            this.MAX_ITEMS_PER_PAGE,
            this.totalItemsLength,
          );
          this.disabled = this.totalItemsLength <= this.MIN_ITEMS_PER_PAGE;

          // Reset pageIndex if it exceeds total pages
          const totalPages = Math.ceil(this.totalItemsLength / this.pageSize);
          if (this.pageIndex + 1 > totalPages && totalPages > 0) {
            this.pageIndex = totalPages - 1;
            this.paginationSubject$.next({
              pageNumber: this.pageIndex + 1,
              pageSize: this.pageSize,
            });
            this.store.dispatch(
              BillingHistoryUpdatePaginationAction({
                pageNumber: this.pageIndex + 1,
                pageSize: this.pageSize,
              }),
            );
          }

          if (this.totalItemsLength === 0) {
            this.pageIndex = 0;
            this.pageSize = this.MIN_ITEMS_PER_PAGE;
            this.disabled = true;
            this.dataSource.data = [];
          }
        } else {
          // Default initialization if no pagination data
          this.pageIndex = 0;
          this.pageSize = this.MIN_ITEMS_PER_PAGE;
          this.totalItemsLength = 0;
          this.pageSizeOptions = [this.MIN_ITEMS_PER_PAGE];
        }
      }),
    );

    // Read tab query param and set default to 'plans'
    this._componentSubscriptions.push(
      this.route.queryParams.subscribe((params) => {
        const tab = params['tab'] as SubscriptionTabs | undefined;
        this.activeTab =
          tab === SubscriptionTabs.BILLING_HISTORY
            ? SubscriptionTabs.BILLING_HISTORY
            : SubscriptionTabs.PLANS;
        // Use last values from store for pagination
        this.pagination$.pipe(take(1)).subscribe((pagination) => {
          const page = pagination ? pagination.pageNumber : 1; // Default to 1 if no store data
          const size = pagination
            ? pagination.pageSize
            : this.MIN_ITEMS_PER_PAGE; // Default to min if no store data
          this.pageIndex = page - 1; // Convert to 0-based index
          this.pageSize = size;
          // Update pagination subject with initial values
          this.paginationSubject$.next({
            pageNumber: this.pageIndex + 1,
            pageSize: this.pageSize,
          });
        });
        // Set initial tab index
        if (this.tabGroup) {
          this.tabGroup.selectedIndex =
            this.activeTab === SubscriptionTabs.PLANS ? 0 : 1;
        }
        // Always dispatch FetchSubscriptionPlansAction
        this.store.dispatch(FetchSubscriptionPlansAction());
      }),
    );

    // Subscribe to the subscriptions$ observable
    this._componentSubscriptions.push(
      this.subscriptions$.subscribe((subscriptions) => {
        this.subscriptions = subscriptions;
        // Filter Out the One Time Plan (like Reading Program)
        this.subscriptions.plans = this.subscriptions.plans.filter(
          (plan) => plan.BillingCycle !== 'one-time',
        );
        // If on billingHistory and subscriptions are now loaded, fetch orders
        if (
          subscriptions.state.status === 'Success' &&
          this.ordersState.status === 'NotFetched'
        ) {
          this.fetchOrdersIfNeeded();
        }
      }),
    );

    // Subscribe to orders$ observable
    this._componentSubscriptions.push(
      this.orders$.subscribe((orders) => {
        const orderWithPlans: IOrderInfoWithMetaData[] = orders.map((order) => {
          // Get the Plan Name (i.e, Billing Cycle From the Plans)
          const plan = this.subscriptions.plans.find(
            (p) => p.Id === order.SubscriptionPlanId,
          );
          return {
            ...order,
            PlanName: plan?.Name || '-',
            CreatedAt: this.formatDateForBillingTable(order.CreatedAt), // Format As per the Design
            Action: this.getRowActionLabel(order),
            FormattedTotalAmount:
              getCurrencySymbol(order.Currency || 'INR') +
              ' ' +
              formatAmount(order.TotalAmount, order.Currency || 'INR'),
            OrderStatusFormattedValue: getOrderStatusLabel(order.Status),
            PaymentStatusFormattedValue: getPaymentStatusLabel(
              order.PaymentStatus,
            ),
            StartDate: this.formatDateForBillingTable(
              order.Subscription?.StartDate || '-',
            ),
            EndDate: this.formatDateForBillingTable(
              order.Subscription?.EndDate || '-',
            ),
          };
        });
        this.dataSource.data = orderWithPlans;
      }),
    );

    // Subscribe to ordersState$ observable
    this._componentSubscriptions.push(
      this.ordersState$.subscribe((state) => {
        this.ordersState = state;
      }),
    );

    // Page-1 orders watcher — drives pending-payment detection on the Plans tab
    this._componentSubscriptions.push(
      this.store
        .select(
          selectOrdersByPageAndFilters(
            1,
            this.MIN_ITEMS_PER_PAGE,
            'all',
            'all',
          ),
        )
        .subscribe((orders) => this.checkAndHandlePendingOrder(orders)),
    );

    // Handle Cancel Auto-Renewal Result (the success effect re-fetches the plans)
    this._componentSubscriptions.push(
      this.actions$
        .pipe(
          ofType(userActions.CancelAutoRenewingSubscriptionSuccessAction),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.isCancellingAutoRenewal = false;
          this.snackBarService.showSnackBar(
            'success',
            'Auto-renewal cancelled. You will not be charged again.',
          );
        }),
    );

    this._componentSubscriptions.push(
      this.actions$
        .pipe(
          ofType(userActions.CancelAutoRenewingSubscriptionFailureAction),
          takeUntil(this.destroy$),
        )
        .subscribe(({ error }) => {
          this.isCancellingAutoRenewal = false;
          this.snackBarService.showSnackBar(
            'error',
            error?.Errors?.[0]?.Message ||
              'Cancelling auto-renewal failed. Please try again.',
          );
        }),
    );

    // Handle Refresh Token Success Action
    this._componentSubscriptions.push(
      this.actions$
        .pipe(
          ofType(userActions.RefreshTokenSuccessAction),
          takeUntil(this.destroy$),
        )
        .subscribe(({ response }) => {
          // Sales may have added or removed the user since the page loaded
          this.loadOrganizationSubscription();
          // The plans depend on the user's subscriptions too - and a request
          // refused with revoked-user (the server asks for a new token after
          // such a change) left them failed, with nothing to fetch them again.
          this.store.dispatch(FetchSubscriptionPlansAction());
          if (this.ordersState.status === 'Failure') {
            this.store.dispatch(
              FetchOrdersAction({
                request: {
                  PageNumber: this.pageIndex + 1,
                  PageSize: this.pageSize,
                },
              }),
            );
          }
          setTimeout(() => {
            this.snackBarService.showSnackBar(
              'success',
              'Subscription Refreshed Successfully.',
              {
                duration: 500,
              },
            );
          }, 200);
        }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
    this._componentSubscriptions.forEach((sub) => sub.unsubscribe());
    this._snackBar.dismiss(); // Close The "Update Email" Snackbar On Component Destroy
    this.showSubscriptionAsDialog = false;
  }
}

@Component({
  selector: 'update-email-snackbar',
  template: `
    <div class="snackbar-content">
      <span class="snackbar-message"
        >Please provide a valid email address to continue</span
      >
      <button class="snackbar-update-email" (click)="updateEmail()">
        You can update your email here
      </button>
      <button class="snackbar-close" (click)="closeSnackbar()">✖</button>
    </div>
  `,
  styles: [
    `
      .snackbar-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
        font-size: 14px;
        font-weight: 500;
        background-color: #fff4e5;
      }
      .snackbar-message {
        flex-grow: 1;
        color: #db4f23;
        margin-left: 10px;
      }
      .snackbar-close {
        background: white;
        border: none;
        color: black;
        font-size: 12px;
        cursor: pointer;
        border-radius: 50%;
        margin-right: 5px;
      }
      .snackbar-update-email {
        border: none;
        color: #464aff;
        background: transparent;
        font-size: 16px;
        cursor: pointer;
        text-decoration: underline;
        margin-right: 5px;
      }
    `,
  ],
})
export class UpdateEmailSnackbarComponent {
  constructor(
    private snackBarRef: MatSnackBarRef<UpdateEmailSnackbarComponent>,
    private router: Router,
  ) {}
  updateEmail() {
    this.snackBarRef.dismiss();
    this.router.navigate(['/profile']);
  }
  closeSnackbar() {
    this.snackBarRef.dismiss();
  }
}
